import { useState, useEffect } from 'react';
import { X, Clock, FileText, AlertTriangle, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/Shared/Button';
import { Input } from '../../../components/Shared/Input';
import { TextArea } from '../../../components/Shared/TextArea';
import { ConfirmDialog } from '../../../components/Shared/ConfirmDialog';
import type { TaskBlock, DayPlanning } from '../../../types/planning';

interface TaskEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskBlock | null;
  currentDay: DayPlanning | null;
  employeeName: string;
  date: string;
  onSave: (taskId: string, updates: {
    startTime: string;
    endTime: string;
    notes?: string;
    applyToFuture: boolean;
  }) => void;
  onDelete?: (taskId: string, planningEntryId: string) => void;
}

export function TaskEditDialog({ isOpen, onClose, task, currentDay, employeeName, date, onSave, onDelete }: TaskEditDialogProps) {
  const { t } = useTranslation('planning');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [notes, setNotes] = useState('');
  const [applyToFuture, setApplyToFuture] = useState(false);
  const [overlapWarning, setOverlapWarning] = useState<string | null>(null);
  const [timeValidationError, setTimeValidationError] = useState<string | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Initialize form when dialog opens or task changes
  useEffect(() => {
    if (isOpen && task) {
      setStartTime(task.startTime);
      setEndTime(task.endTime);
      setNotes(''); // Could load from backend if available
      setApplyToFuture(false);
      setOverlapWarning(null);
      setTimeValidationError(null);
      setIsDeleteConfirmOpen(false);
    }
  }, [isOpen, task]);

  // Validate times and check for overlaps
  useEffect(() => {
    if (!startTime || !endTime || !currentDay || !task) {
      setOverlapWarning(null);
      setTimeValidationError(null);
      return;
    }

    // Validate start < end
    if (startTime >= endTime) {
      setTimeValidationError('Startzeit muss vor der Endzeit liegen');
      setOverlapWarning(null);
      return;
    }

    setTimeValidationError(null);

    // Check for overlaps with other tasks in the same day
    const overlappingTask = currentDay.tasks.find((existingTask) => {
      // Skip the task being edited
      if (existingTask.id === task.id) return false;

      const newStart = startTime;
      const newEnd = endTime;
      const existingStart = existingTask.startTime;
      const existingEnd = existingTask.endTime;

      // Overlap occurs if: (newStart < existingEnd) AND (newEnd > existingStart)
      return newStart < existingEnd && newEnd > existingStart;
    });

    if (overlappingTask) {
      setOverlapWarning(
        `Überschneidung mit "${overlappingTask.taskName}" (${overlappingTask.startTime} - ${overlappingTask.endTime})`
      );
    } else {
      setOverlapWarning(null);
    }
  }, [startTime, endTime, currentDay, task]);

  if (!isOpen || !task) return null;

  const handleSave = () => {
    if (timeValidationError || overlapWarning) return;
    
    onSave(task.id, {
      startTime,
      endTime,
      notes: notes || undefined,
      applyToFuture,
    });
  };

  const handleConfirmDelete = () => {
    if (!onDelete || !currentDay?.planningEntryId) return;
    setIsDeleteConfirmOpen(false);
    onDelete(task.id, currentDay.planningEntryId);
  };

  const calculateDuration = () => {
    if (!startTime || !endTime) return '0h';
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const minutes = (endH * 60 + endM) - (startH * 60 + startM);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const hasError = !!timeValidationError;
  const hasWarning = !!overlapWarning;
  const canSave = !hasError && !hasWarning && startTime && endTime;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Aufgabe bearbeiten
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Task Info */}
          <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-2">
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Aufgabe:</span>
              <p className="font-semibold text-gray-900 dark:text-white">{task.taskName}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Objekt:</span>
              <p className="text-sm text-gray-700 dark:text-gray-300">{task.objectName}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Leistung:</span>
              <p className="text-sm text-gray-700 dark:text-gray-300">{task.serviceName}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Mitarbeiter:</span>
              <p className="text-sm text-gray-700 dark:text-gray-300">{employeeName}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Datum:</span>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {new Date(date).toLocaleDateString('de-DE', {
                  weekday: 'long',
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </p>
            </div>
          </div>

          {/* Time Inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Startzeit
              </label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className={hasError ? 'border-red-500' : ''}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Endzeit
              </label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className={hasError ? 'border-red-500' : ''}
              />
            </div>
          </div>

          {/* Validation Error */}
          {hasError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">Ungültige Zeitangabe</p>
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">{timeValidationError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Overlap Warning */}
          {hasWarning && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Zeitüberschneidung erkannt</p>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">{overlapWarning}</p>
                  <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-2">
                    Das Backend wird diese Änderung ablehnen. Bitte passen Sie die Zeiten an.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Duration Display */}
          {startTime && endTime && !hasError && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Dauer: <span className="font-semibold">{calculateDuration()}</span>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Notiz (optional)
            </label>
            <TextArea
              label=""
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Besonderheiten, Bemerkungen..."
              rows={3}
            />
          </div>

          {/* Apply to Future */}
          <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <input
              type="checkbox"
              id="applyToFuture"
              checked={applyToFuture}
              onChange={(e) => setApplyToFuture(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label htmlFor="applyToFuture" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              Änderung auf alle zukünftigen gleichen Aufgaben anwenden
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          {onDelete && currentDay?.planningEntryId ? (
            <Button variant="danger" onClick={() => setIsDeleteConfirmOpen(true)}>
              <Trash2 className="w-4 h-4 mr-2" />
              {t('actions.remove')}
            </Button>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button 
              variant="primary"
              onClick={handleSave}
              disabled={!canSave}
            >
              Speichern
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('messages.removeTaskTitle')}
        message={t('messages.removeTaskConfirm')}
        confirmText={t('actions.remove')}
        variant="danger"
      />
    </div>
  );
}

